// Chamadas à API já autenticadas: token e instituição ativa entram sozinhos.
//
//   const api = useApi();
//   const alunos = await api.get('/api/alunos');
//   await api.post('/api/listas', { nome });
//
// Resposta de erro vira exceção com a mensagem do backend (err.message) e o
// status HTTP (err.status). Para arquivos (PDF, Excel), use api.raw(), que
// devolve o Response sem ler o corpo.
import { useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useInstitution } from '../contexts/InstitutionContext';
import { API_URL } from './config';

export class ApiError extends Error {
  constructor(message, status, dados) {
    super(message);
    this.status = status;
    this.dados = dados;
  }
}

async function lerCorpo(res) {
  if (res.status === 204) return null;
  const texto = await res.text();
  if (!texto) return null;
  try {
    return JSON.parse(texto);
  } catch {
    return texto;
  }
}

export function useApi() {
  const { token } = useAuth();
  const { institutionId } = useInstitution();

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      ...(institutionId && { 'x-institution-id': institutionId }),
    }),
    [token, institutionId],
  );

  const raw = useCallback(
    (caminho, init = {}) =>
      fetch(`${API_URL}${caminho}`, { ...init, headers: { ...headers, ...init.headers } }),
    [headers],
  );

  const request = useCallback(
    async (method, caminho, corpo) => {
      const init = { method };
      if (corpo !== undefined) {
        init.body = JSON.stringify(corpo);
        init.headers = { 'Content-Type': 'application/json' };
      }
      const res = await raw(caminho, init);
      const dados = await lerCorpo(res);
      if (!res.ok) {
        const mensagem = dados?.error || 'Não foi possível concluir a operação. Tente novamente.';
        throw new ApiError(mensagem, res.status, dados);
      }
      return dados;
    },
    [raw],
  );

  return useMemo(
    () => ({
      headers,
      raw,
      get: (caminho) => request('GET', caminho),
      post: (caminho, corpo) => request('POST', caminho, corpo),
      put: (caminho, corpo) => request('PUT', caminho, corpo),
      patch: (caminho, corpo) => request('PATCH', caminho, corpo),
      del: (caminho) => request('DELETE', caminho),
    }),
    [headers, raw, request],
  );
}
